(() => {
  "use strict";

  const SELECTOR = "[data-mk-map-config-map]";
  const instances = new WeakMap();

  class MKMapConfigMap {
    constructor(section) {
      this.section = section;
      this.map = null;
      this.geocoder = null;
      this.resizeObserver = null;
      this.rafId = null;
      this.destroyed = false;
      this.ipCenter = null;
      this.customerMovedMap = false;
      this.startupZoomComplete = false;
      this.ipControllers = [];
      this.section.dataset.ipLocationState = "loading";
      this.config = this.readConfig();
      this.state = {
        orientation: this.normalizeOrientation(this.config.initialOrientation),
        loaded: false,
        valid: false
      };

      this.elements = {
        mapWrapper: section.querySelector("[data-mk-map-wrapper]"),
        mapWindow: section.querySelector("[data-mk-map-window]"),
        mapbox: section.querySelector("[data-mk-mapbox]"),
        search: section.querySelector("[data-mk-search]"),
        orientationButtons: Array.from(
          section.querySelectorAll("[data-mk-orientation-button]")
        ),
        validationRectangle: section.querySelector(
          "[data-mk-validation-rectangle]"
        ),
        validationText: section.querySelector("[data-mk-validation-text]"),
        continueButton: section.querySelector("[data-mk-continue]"),
        productForm: section.querySelector("[data-mk-product-form]"),
        variantInput: section.querySelector("[data-mk-variant-id]"),
        properties: {
          orientation: section.querySelector(
            '[data-mk-property="orientation"]'
          ),
          center: section.querySelector('[data-mk-property="center"]'),
          bounds: section.querySelector('[data-mk-property="bounds"]'),
          blosm: section.querySelector('[data-mk-property="blosm"]'),
          zoom: section.querySelector('[data-mk-property="zoom"]'),
          widthKm: section.querySelector('[data-mk-property="width-km"]'),
          heightKm: section.querySelector('[data-mk-property="height-km"]'),
          thumbnail: section.querySelector('[data-mk-property="thumbnail"]')
        }
      };

      this.bound = {
        onOrientationClick: this.onOrientationClick.bind(this),
        onMapMovement: this.scheduleUpdate.bind(this),
        onResize: this.onResize.bind(this),
        onSubmit: this.onSubmit.bind(this),
        onGeocoderResult: this.onGeocoderResult.bind(this),
        onGeocoderError: this.onGeocoderError.bind(this),
        onCustomerMapInteraction: (event) => {
          if (event && event.originalEvent) this.customerMovedMap = true;
        }
      };
    }

    init() {
      if (!this.hasRequiredElements()) {
        return;
      }

      this.updateWrapperSize();
      this.applyOrientation(this.state.orientation, false);
      this.bindDOMEvents();

      if (!this.elements.productForm) {
        this.setUnavailable(this.config.productMissingText);
      }

      const ipLookup = this.getIpCenter().then((center) => {
        if (!center || this.destroyed) return;
        this.ipCenter = center;
        if (this.map && !this.customerMovedMap) {
          this.map.setCenter(center);
          this.section.dataset.ipLocationState = "applied";
          if (this.state.loaded) {
            this.startupZoomComplete = false;
            this.fitStartingSelection();
            this.updateSelection();
          }
        } else if (this.map) {
          this.section.dataset.ipLocationState = "skipped-after-interaction";
        }
      });

      Promise.all([
        this.waitForMapbox(),
        Promise.race([
          ipLookup,
          new Promise((resolve) => window.setTimeout(resolve, 1400))
        ])
      ])
        .then(() => {
          if (!this.destroyed) {
            this.createMap(this.ipCenter);
          }
        })
        .catch(() => {
          if (!this.destroyed) {
            this.setUnavailable(this.config.mapErrorText);
          }
        });
    }

    readConfig() {
      const data = this.section.dataset;

      return {
        hasOrientationVariants: data.hasOrientationVariants === "true",
        orientationVariants: {
          vertical: data.verticalVariantId || "",
          horizontal: data.horizontalVariantId || ""
        },
        orientationAvailable: {
          vertical: data.verticalVariantAvailable !== "false",
          horizontal: data.horizontalVariantAvailable !== "false"
        },
        token: data.mapboxToken || "",
        style: data.mapboxStyle || "mapbox://styles/mapbox/streets-v12",
        initialLat: this.toNumber(data.initialLat, 38.7223),
        initialLng: this.toNumber(data.initialLng, -9.1393),
        initialZoom: this.toNumber(data.initialZoom, 13),
        minKm: this.toNumber(data.minKm, 0.5),
        maxKm: this.toNumber(data.maxKm, 2),
        initialOrientation: data.initialOrientation || "vertical",
        validText: data.validText || "Selection ready.",
        invalidSmallText:
          data.invalidSmallText ||
          "Zoom out. The selected area is too small.",
        invalidLargeText:
          data.invalidLargeText ||
          "Zoom in. The selected area is too large.",
        loadingText: data.loadingText || "Loading map…",
        mapErrorText: data.mapErrorText || "The map could not be loaded.",
        productMissingText:
          data.productMissingText || "Select a product in the Theme Editor.",
        overallSizeDesktop: this.toNumber(data.overallSizeDesktop, 720),
        overallSizeMobile: this.toNumber(data.overallSizeMobile, 340),
        verticalRatio: this.toNumber(data.verticalRatio, 2 / 3),
        horizontalRatio: this.toNumber(data.horizontalRatio, 3 / 2)
      };
    }

    hasRequiredElements() {
      return Boolean(
        this.elements.mapWrapper &&
          this.elements.mapWindow &&
          this.elements.mapbox &&
          this.elements.validationRectangle &&
          this.elements.validationText
      );
    }

    waitForMapbox() {
      return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        const timeoutMs = 12000;

        const check = () => {
          if (
            window.mapboxgl &&
            typeof window.mapboxgl.Map === "function" &&
            typeof window.MapboxGeocoder === "function"
          ) {
            resolve();
            return;
          }

          if (Date.now() - startedAt >= timeoutMs) {
            reject(new Error("Mapbox libraries did not load."));
            return;
          }

          window.setTimeout(check, 50);
        };

        check();
      });
    }

    async getIpCenter() {
      const providers = [
        { name: "geojs", url: "https://get.geojs.io/v1/ip/geo.json" },
        { name: "ipwhois", url: "https://ipwho.is/" }
      ];
      const requests = providers.map(async (provider) => {
        const controller = new AbortController();
        this.ipControllers.push(controller);
        const timeout = window.setTimeout(() => controller.abort(), 4000);

        try {
          const response = await fetch(provider.url, {
            method: "GET",
            mode: "cors",
            credentials: "omit",
            cache: "no-store",
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const location = await response.json();
          if (!location || location.error || location.success === false) {
            throw new Error(location && location.message || "Location unavailable");
          }
          if (location.latitude == null || location.longitude == null ||
              String(location.latitude).trim() === "" ||
              String(location.longitude).trim() === "") {
            throw new Error("Missing coordinates");
          }

          const lat = Number(location.latitude);
          const lng = Number(location.longitude);
          if (
            !Number.isFinite(lat) ||
            !Number.isFinite(lng) ||
            lat < -85 || lat > 85 ||
            lng < -180 || lng > 180
          ) throw new Error("Invalid coordinates");

          return { center: [lng, lat], provider: provider.name, location };
        } catch (error) {
          this.section.dataset[provider.name + "LocationError"] = error.message;
          throw error;
        } finally {
          window.clearTimeout(timeout);
        }
      });

      try {
        const result = await Promise.any(requests);
        if (this.destroyed) return null;
        this.section.dataset.ipCenterSource = result.provider;
        this.section.dataset.ipLocationState = "resolved";
        this.section.dataset.ipLocationCity = result.location.city || "";
        this.section.dataset.ipLocationCountry = result.location.country || "";
        return result.center;
      } catch (error) {
        if (!this.destroyed) {
          this.section.dataset.ipCenterSource = "fallback";
          this.section.dataset.ipLocationState = "unavailable";
          console.warn("MK Configurator: IP lookup failed; using configured location.");
        }
        return null;
      } finally {
        this.ipControllers.forEach(controller => controller.abort());
        this.ipControllers = [];
      }
    }

    createMap(ipCenter) {
      if (!this.config.token) {
        this.setUnavailable(this.config.mapErrorText);
        return;
      }

      window.mapboxgl.accessToken = this.config.token;

      try {
        this.map = new window.mapboxgl.Map({
          container: this.elements.mapbox,
          style: this.config.style,
          center: ipCenter || [this.config.initialLng, this.config.initialLat],
          zoom: this.config.initialZoom,
          attributionControl: true,
          cooperativeGestures: false
        });

        this.map.dragRotate.disable();
this.map.touchZoomRotate.disableRotation();

      } catch (error) {
        console.error("MK Configurator: Mapbox initialization failed.", error);
        this.setUnavailable(this.config.mapErrorText);
        return;
      }

      this.map.addControl(
        new window.mapboxgl.NavigationControl({
          showCompass: false,
          visualizePitch: false
        }),
        "top-right"
      );
      if (ipCenter) this.section.dataset.ipLocationState = "applied";

      this.createGeocoder();
      this.bindMapEvents();
      this.map.on("dragstart", this.bound.onCustomerMapInteraction);
      this.map.on("zoomstart", this.bound.onCustomerMapInteraction);
      this.observeGeometry();

      this.map.once("load", () => {
        if (this.destroyed) {
          return;
        }

        this.state.loaded = true;
        this.map.resize();
        this.fitStartingSelection();
        this.updateSelection();
      });

      this.map.on("error", (event) => {
        if (!this.state.loaded && event && event.error) {
          console.error("MK Configurator: Mapbox error.", event.error);
        }
      });
    }

createGeocoder() {
  if (!this.elements.search || !this.map) {
    return;
  }

  this.elements.search.innerHTML = "";

  this.geocoder = new window.MapboxGeocoder({
    accessToken: this.config.token,
    mapboxgl: window.mapboxgl,
    marker: false,
    flyTo: false,
    useBrowserFocus: true,
    proximity: "ip",
    trackProximity: false,
    limit: 8,
    placeholder: "Search for a place",
    types:
      "country,region,postcode,district,place,locality,neighborhood,address,poi"
  });

  this.geocoder.addTo(this.elements.search);

  this.geocoder.on("result", (event) => {
    this.elements.search.classList.remove("mk-geocoder-is-typing");
    this.bound.onGeocoderResult(event);
  });

  this.geocoder.on("error", this.bound.onGeocoderError);

  const input = this.elements.search.querySelector(
    ".mapboxgl-ctrl-geocoder--input"
  );

  if (!input) {
    return;
  }

  input.setAttribute("autocomplete", "off");

  const updateSuggestionState = () => {
    const hasSearchText = input.value.trim().length >= 2;

    this.elements.search.classList.toggle(
      "mk-geocoder-is-typing",
      hasSearchText
    );
  };

  input.addEventListener("input", updateSuggestionState);
  input.addEventListener("focus", updateSuggestionState);

  input.addEventListener("blur", () => {
    /*
      Delay hiding so that clicking a suggestion still works.
    */
    window.setTimeout(() => {
      this.elements.search.classList.remove("mk-geocoder-is-typing");
    }, 250);
  });
}

    onGeocoderResult(event) {
      if (!this.map || !event || !event.result) {
        return;
      }

      const result = event.result;
      const center =
        Array.isArray(result.center) && result.center.length >= 2
          ? result.center
          : result.geometry && Array.isArray(result.geometry.coordinates)
            ? result.geometry.coordinates
            : null;

      if (!center) {
        return;
      }

      this.customerMovedMap = true;

      if (
        Array.isArray(result.bbox) &&
        result.bbox.length === 4 &&
        result.bbox.every(Number.isFinite)
      ) {
        this.map.fitBounds(
          [
            [result.bbox[0], result.bbox[1]],
            [result.bbox[2], result.bbox[3]]
          ],
          {
            padding: 60,
            maxZoom: Math.max(this.config.initialZoom, 14),
            duration: 900
          }
        );
      } else {
        this.map.flyTo({
          center,
          zoom: Math.max(this.config.initialZoom, 14),
          duration: 900,
          essential: true
        });
      }
    }

    onGeocoderError(event) {
      if (event && event.error) {
        console.error("MK Configurator: Geocoder error.", event.error);
      }
    }

    bindDOMEvents() {
      this.elements.orientationButtons.forEach((button) => {
        button.addEventListener("click", this.bound.onOrientationClick);
      });

      if (this.elements.productForm) {
        this.elements.productForm.addEventListener(
          "submit",
          this.bound.onSubmit
        );
      }
    }

    bindMapEvents() {
      ["move", "zoom", "rotate", "pitch"].forEach((eventName) => {
        this.map.on(eventName, this.bound.onMapMovement);
      });

      this.map.on("moveend", this.bound.onMapMovement);
      this.map.on("zoomend", this.bound.onMapMovement);
    }

    observeGeometry() {
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.bound.onResize);
        this.resizeObserver.observe(this.elements.mapWrapper);
        this.resizeObserver.observe(this.elements.mapWindow);
        this.resizeObserver.observe(this.elements.validationRectangle);
      } else {
        window.addEventListener("resize", this.bound.onResize);
      }
    }

    onOrientationClick(event) {
      this.customerMovedMap = true;
      const orientation = this.normalizeOrientation(
        event.currentTarget.dataset.mkOrientationButton
      );

      if (orientation === this.state.orientation) {
        return;
      }

      this.applyOrientation(orientation, true);
    }

    applyOrientation(orientation, shouldResizeMap) {
      this.state.orientation = orientation;
      this.elements.mapWrapper.dataset.orientation = orientation;
      this.updateWrapperSize();

      this.elements.orientationButtons.forEach((button) => {
        const isActive =
          button.dataset.mkOrientationButton === this.state.orientation;
        button.setAttribute("aria-pressed", String(isActive));
      });

      this.writeProperty("orientation", this.capitalize(orientation));
      this.syncOrientationVariant();

      if (shouldResizeMap && this.map) {
        window.requestAnimationFrame(() => {
          if (!this.map || this.destroyed) {
            return;
          }

          this.map.resize();

          window.requestAnimationFrame(() => {
            if (!this.map || this.destroyed) {
              return;
            }

            this.map.resize();
            this.updateSelection();
          });
        });
      }
    }

    onResize() {
      if (this.destroyed) {
        return;
      }

      this.updateWrapperSize();

      if (!this.map) {
        return;
      }

      this.map.resize();
      if (this.state.loaded && !this.customerMovedMap) {
        this.startupZoomComplete = false;
        this.fitStartingSelection();
      }
      this.scheduleUpdate();
    }

    fitStartingSelection() {
      if (!this.map || !this.state.loaded || this.destroyed ||
          this.customerMovedMap || this.startupZoomComplete) return;

      const minKm = this.config.minKm;
      const maxKm = this.config.maxKm;
      if (!Number.isFinite(minKm) || !Number.isFinite(maxKm) ||
          minKm <= 0 || maxKm < minKm) return;

      const initialZoom = Math.max(
        this.map.getMinZoom(),
        Math.min(this.map.getMaxZoom(), this.config.initialZoom)
      );
      if (Math.abs(this.map.getZoom() - initialZoom) > 0.0001) {
        this.map.setZoom(initialZoom);
      }

      let adjusted = false;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const selection = this.getSelectionGeometry();
        if (!selection) return;
        const longestKm = Math.max(selection.widthKm, selection.heightKm);
        if (!Number.isFinite(longestKm) || longestKm <= 0) return;
        if (longestKm >= minKm && longestKm <= maxKm) break;

        const marginKm = Math.min((maxKm - minKm) / 4, minKm * 0.005);
        const targetKm = longestKm < minKm ? minKm + marginKm : maxKm - marginKm;
        const zoom = this.map.getZoom() + Math.log2(longestKm / targetKm);
        const clamped = Math.max(this.map.getMinZoom(), Math.min(this.map.getMaxZoom(), zoom));
        if (Math.abs(clamped - this.map.getZoom()) < 0.0001) break;
        adjusted = true;
        this.map.setZoom(clamped);
      }

      const finalSelection = this.getSelectionGeometry();
      if (!finalSelection) return;
      const longestKm = Math.max(finalSelection.widthKm, finalSelection.heightKm);
      this.startupZoomComplete = longestKm >= minKm && longestKm <= maxKm;
      this.section.dataset.startingSelectionKm = longestKm.toFixed(3);
      this.section.dataset.startingZoomState = this.startupZoomComplete ? "valid" : "unavailable";
      this.section.dataset.startingZoomSource = adjusted ? "adjusted-to-valid-range" : "theme-setting";
      this.section.dataset.startingZoom = this.map.getZoom().toFixed(3);
    }

    scheduleUpdate() {
      if (!this.map || !this.state.loaded || this.destroyed) {
        return;
      }

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateSelection();
      });
    }

    updateSelection() {
      if (!this.syncOrientationVariant()) {
        this.setUnavailable("This orientation is currently unavailable.");
        return;
      }
      const selection = this.getSelectionGeometry();

      if (!selection) {
        this.setUnavailable(this.config.mapErrorText);
        return;
      }

      const mainSideKm = Math.max(selection.widthKm, selection.heightKm);
      const isTooSmall = mainSideKm < this.config.minKm;
      const isTooLarge = mainSideKm > this.config.maxKm;
      const isValid = !isTooSmall && !isTooLarge;

      this.state.valid = isValid;
      this.section.dataset.validationState = isValid ? "valid" : "invalid";

      if (isTooSmall) {
        this.elements.validationText.textContent =
          this.config.invalidSmallText;
      } else if (isTooLarge) {
        this.elements.validationText.textContent =
          this.config.invalidLargeText;
      } else {
        this.elements.validationText.textContent = this.config.validText;
      }

      if (this.elements.continueButton) {
        this.elements.continueButton.disabled = !isValid;
      }

      this.writeSelectionProperties(selection);
    }

    getSelectionGeometry() {
      if (!this.map) {
        return null;
      }

      const mapRect = this.elements.mapbox.getBoundingClientRect();
      const selectionRect =
        this.elements.validationRectangle.getBoundingClientRect();

      if (
        mapRect.width <= 0 ||
        mapRect.height <= 0 ||
        selectionRect.width <= 0 ||
        selectionRect.height <= 0
      ) {
        return null;
      }

      const left = selectionRect.left - mapRect.left;
      const right = selectionRect.right - mapRect.left;
      const top = selectionRect.top - mapRect.top;
      const bottom = selectionRect.bottom - mapRect.top;

      const northwest = this.map.unproject([left, top]);
      const northeast = this.map.unproject([right, top]);
      const southeast = this.map.unproject([right, bottom]);
      const southwest = this.map.unproject([left, bottom]);

      const widthTopKm = this.haversineKm(northwest, northeast);
      const widthBottomKm = this.haversineKm(southwest, southeast);
      const heightLeftKm = this.haversineKm(northwest, southwest);
      const heightRightKm = this.haversineKm(northeast, southeast);

      const widthKm = (widthTopKm + widthBottomKm) / 2;
      const heightKm = (heightLeftKm + heightRightKm) / 2;

      const west = Math.min(
        northwest.lng,
        northeast.lng,
        southeast.lng,
        southwest.lng
      );
      const east = Math.max(
        northwest.lng,
        northeast.lng,
        southeast.lng,
        southwest.lng
      );
      const south = Math.min(
        northwest.lat,
        northeast.lat,
        southeast.lat,
        southwest.lat
      );
      const north = Math.max(
        northwest.lat,
        northeast.lat,
        southeast.lat,
        southwest.lat
      );

      const center = this.map.getCenter();

      return {
        center,
        west,
        south,
        east,
        north,
        widthKm,
        heightKm,
        zoom: this.map.getZoom()
      };
    }

    writeSelectionProperties(selection) {
      this.writeProperty(
        "center",
        `${selection.center.lat.toFixed(6)}, ${selection.center.lng.toFixed(6)}`
      );

      this.writeProperty(
        "bounds",
        [
          selection.west.toFixed(6),
          selection.south.toFixed(6),
          selection.east.toFixed(6),
          selection.north.toFixed(6)
        ].join(",")
      );

      this.writeProperty(
        "blosm",
        [
          selection.west.toFixed(6),
          selection.south.toFixed(6),
          selection.east.toFixed(6),
          selection.north.toFixed(6)
        ].join(",")
      );

      this.writeProperty("zoom", selection.zoom.toFixed(2));
      this.writeProperty("widthKm", selection.widthKm.toFixed(3));
      this.writeProperty("heightKm", selection.heightKm.toFixed(3));
      this.writeProperty("thumbnail", this.buildThumbnailUrl(selection));
    }

    buildThumbnailUrl(selection) {
      const style = this.config.style.replace(/^mapbox:\/\/styles\//, "").trim();
      const token = this.config.token;
      if (!style || !token || !selection) return "";
      const rect = this.elements.validationRectangle.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return "";
      const ratio = rect.width / rect.height;
      const width = ratio >= 1 ? 1200 : Math.max(1, Math.round(1200 * ratio));
      const height = ratio >= 1 ? Math.max(1, Math.round(1200 / ratio)) : 1200;
      const bounds = [selection.west, selection.south, selection.east, selection.north];
      if (!bounds.every(Number.isFinite)) return "";
      const bbox = bounds.map(value => value.toFixed(6)).join(",");
      return `https://api.mapbox.com/styles/v1/${style}/static/[${bbox}]/${width}x${height}@2x?padding=0&access_token=${encodeURIComponent(token)}`;
    }

    writeProperty(key, value) {
      const element = this.elements.properties[key];

      if (element) {
        element.value = value;
      }
    }

    setUnavailable(message) {
      this.state.valid = false;
      this.section.dataset.validationState = "invalid";
      this.elements.validationText.textContent = message;

      if (this.elements.continueButton) {
        this.elements.continueButton.disabled = true;
      }
    }

    syncOrientationVariant() {
      const input = this.elements.variantInput;
      if (!input) return false;
      const orientation = this.state.orientation;
      const variantId = this.config.orientationVariants[orientation] ||
        (!this.config.hasOrientationVariants ? input.value : "");
      const available = this.config.orientationAvailable[orientation] !== false;
      input.value = variantId;
      this.section.dataset.selectedVariantId = variantId;
      return Boolean(variantId && available);
    }

    onSubmit(event) {
      this.updateSelection();
      if (!this.state.valid || !this.syncOrientationVariant()) {
        event.preventDefault();
      }
    }

    haversineKm(pointA, pointB) {
      const earthRadiusKm = 6371.0088;
      const lat1 = this.toRadians(pointA.lat);
      const lat2 = this.toRadians(pointB.lat);
      const deltaLat = this.toRadians(pointB.lat - pointA.lat);
      const deltaLng = this.toRadians(pointB.lng - pointA.lng);

      const a =
        Math.sin(deltaLat / 2) ** 2 +
        Math.cos(lat1) *
          Math.cos(lat2) *
          Math.sin(deltaLng / 2) ** 2;

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return earthRadiusKm * c;
    }

    updateWrapperSize() {
      if (!this.elements.mapWrapper) {
        return;
      }

      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      const equivalentSquare = isMobile
        ? this.config.overallSizeMobile
        : this.config.overallSizeDesktop;

      const ratio =
        this.state.orientation === "horizontal"
          ? this.config.horizontalRatio
          : this.config.verticalRatio;

      const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
      const width = equivalentSquare * Math.sqrt(safeRatio);

      this.section.style.setProperty(
        "--mk-wrapper-width",
        `${Math.round(width)}px`
      );
    }

    normalizeOrientation(value) {
      return value === "horizontal" ? "horizontal" : "vertical";
    }

    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    toRadians(value) {
      return (value * Math.PI) / 180;
    }

    toNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }

    destroy() {
      this.destroyed = true;
      this.ipControllers.forEach(controller => controller.abort());
      this.ipControllers = [];

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      this.elements.orientationButtons.forEach((button) => {
        button.removeEventListener("click", this.bound.onOrientationClick);
      });

      if (this.elements.productForm) {
        this.elements.productForm.removeEventListener(
          "submit",
          this.bound.onSubmit
        );
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", this.bound.onResize);
      }

      if (this.geocoder) {
        if (typeof this.geocoder.off === "function") {
          this.geocoder.off("result", this.bound.onGeocoderResult);
          this.geocoder.off("error", this.bound.onGeocoderError);
        }

        if (typeof this.geocoder.onRemove === "function") {
          this.geocoder.onRemove();
        }

        this.geocoder = null;
      }

      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    }
  }

  function initializeSection(section) {
    if (!(section instanceof HTMLElement) || instances.has(section)) {
      return;
    }

    const instance = new MKMapConfigMap(section);
    instances.set(section, instance);
    instance.init();
  }

  function initializeAll(root = document) {
    if (root.matches && root.matches(SELECTOR)) {
      initializeSection(root);
    }

    root.querySelectorAll(SELECTOR).forEach(initializeSection);
  }

  function destroySection(section) {
    const instance = instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const section =
      event.target.matches && event.target.matches(SELECTOR)
        ? event.target
        : event.target.querySelector(SELECTOR);

    if (section) {
      destroySection(section);
    }
  });
})();



